import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Edit, Save, X, Upload, Users, FolderOpen, RefreshCw, Lock, KeyRound } from 'lucide-react';
import { getTeamMembers, getProjects, getCategories } from '../api/api';
import { adminAPI } from '../api/admin';

// Admin access key - change this to your secret key
const ADMIN_KEY = 'archifab@2024';

const Admin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [keyInput, setKeyInput] = useState('');
  const [keyError, setKeyError] = useState('');
  const [activeTab, setActiveTab] = useState('team');
  const [team, setTeam] = useState([]);
  const [projects, setProjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Check if already authenticated (session storage)
  useEffect(() => {
    const savedAuth = sessionStorage.getItem('archifab_admin_auth');
    if (savedAuth === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated]);

  const handleKeySubmit = (e) => {
    e.preventDefault();
    if (keyInput === ADMIN_KEY) {
      setIsAuthenticated(true);
      sessionStorage.setItem('archifab_admin_auth', 'true');
      setKeyError('');
    } else {
      setKeyError('Invalid access key. Please try again.');
      setKeyInput('');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('archifab_admin_auth');
  };

  // Key Entry Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-100 rounded-full mb-4">
              <Lock className="w-8 h-8 text-amber-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">ARCHIFAB Admin</h1>
            <p className="text-gray-500 mt-2">Enter access key to continue</p>
          </div>
          
          <form onSubmit={handleKeySubmit}>
            <div className="relative mb-4">
              <KeyRound className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="password"
                value={keyInput}
                onChange={(e) => setKeyInput(e.target.value)}
                placeholder="Enter access key"
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none transition"
                autoFocus
              />
            </div>
            
            {keyError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                {keyError}
              </div>
            )}
            
            <button
              type="submit"
              className="w-full bg-amber-600 hover:bg-amber-700 text-white font-semibold py-3 rounded-lg transition duration-200"
            >
              Access Admin Panel
            </button>
          </form>
          
          <p className="text-center text-gray-400 text-sm mt-6">
            Contact administrator if you've forgotten the key
          </p>
        </div>
      </div>
    );
  }

  const fetchData = async () => {
    setLoading(true);
    try {
      const [teamData, projectsData, categoriesData] = await Promise.all([
        getTeamMembers(),
        getProjects(),
        getCategories()
      ]);
      setTeam(teamData);
      setProjects(projectsData);
      setCategories(categoriesData);
    } catch (error) {
      showMessage('error', 'Failed to load data');
    }
    setLoading(false);
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  // Team Member Form
  const TeamForm = ({ item, onSave, onCancel }) => {
    const [form, setForm] = useState(item || {
      name: '', role: '', image: '', bio: '', linkedin: '', email: '', order: 0
    });

    return (
      <div className="bg-gray-50 p-4 rounded-lg mb-4">
        <div className="grid grid-cols-2 gap-4">
          <input
            className="border p-2 rounded"
            placeholder="Name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <input
            className="border p-2 rounded"
            placeholder="Role (e.g., Principal Architect)"
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value })}
          />
          <input
            className="border p-2 rounded"
            placeholder="Image URL"
            value={form.image}
            onChange={(e) => setForm({ ...form, image: e.target.value })}
          />
          <input
            className="border p-2 rounded"
            placeholder="LinkedIn URL"
            value={form.linkedin || ''}
            onChange={(e) => setForm({ ...form, linkedin: e.target.value })}
          />
          <input
            className="border p-2 rounded"
            placeholder="Email"
            value={form.email || ''}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
          <input
            className="border p-2 rounded"
            type="number"
            placeholder="Display Order"
            value={form.order || 0}
            onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) })}
          />
          <textarea
            className="border p-2 rounded col-span-2"
            placeholder="Bio"
            rows={3}
            value={form.bio || ''}
            onChange={(e) => setForm({ ...form, bio: e.target.value })}
          />
        </div>
        <div className="flex gap-2 mt-4">
          <button
            onClick={() => onSave(form)}
            className="bg-green-600 text-white px-4 py-2 rounded flex items-center gap-2"
          >
            <Save size={16} /> Save
          </button>
          <button
            onClick={onCancel}
            className="bg-gray-400 text-white px-4 py-2 rounded flex items-center gap-2"
          >
            <X size={16} /> Cancel
          </button>
        </div>
      </div>
    );
  };

  // Project Form
  const ProjectForm = ({ item, onSave, onCancel }) => {
    const [form, setForm] = useState(item || {
      title: '', category: '', location: '', year: '', description: '', images: [''], featured: false
    });

    const addImageField = () => {
      setForm({ ...form, images: [...form.images, ''] });
    };

    const updateImage = (index, value) => {
      const newImages = [...form.images];
      newImages[index] = value;
      setForm({ ...form, images: newImages });
    };

    const removeImage = (index) => {
      const newImages = form.images.filter((_, i) => i !== index);
      setForm({ ...form, images: newImages });
    };

    return (
      <div className="bg-gray-50 p-4 rounded-lg mb-4">
        <div className="grid grid-cols-2 gap-4">
          <input
            className="border p-2 rounded"
            placeholder="Project Title"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
          />
          <select
            className="border p-2 rounded"
            value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value })}
          >
            <option value="">Select Category</option>
            {categories.map(cat => (
              <option key={cat.slug} value={cat.slug}>{cat.name}</option>
            ))}
          </select>
          <input
            className="border p-2 rounded"
            placeholder="Location"
            value={form.location || ''}
            onChange={(e) => setForm({ ...form, location: e.target.value })}
          />
          <input
            className="border p-2 rounded"
            placeholder="Year"
            value={form.year || ''}
            onChange={(e) => setForm({ ...form, year: e.target.value })}
          />
          <textarea
            className="border p-2 rounded col-span-2"
            placeholder="Description"
            rows={3}
            value={form.description || ''}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
          <div className="col-span-2">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.featured || false}
                onChange={(e) => setForm({ ...form, featured: e.target.checked })}
              />
              Featured Project
            </label>
          </div>
          <div className="col-span-2">
            <label className="font-semibold mb-2 block">Images:</label>
            {form.images.map((img, index) => (
              <div key={index} className="flex gap-2 mb-2">
                <input
                  className="border p-2 rounded flex-1"
                  placeholder="Image URL"
                  value={img}
                  onChange={(e) => updateImage(index, e.target.value)}
                />
                {form.images.length > 1 && (
                  <button
                    onClick={() => removeImage(index)}
                    className="text-red-500 p-2"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            ))}
            <button
              onClick={addImageField}
              className="text-blue-600 text-sm flex items-center gap-1"
            >
              <Plus size={14} /> Add Image
            </button>
          </div>
        </div>
        <div className="flex gap-2 mt-4">
          <button
            onClick={() => onSave(form)}
            className="bg-green-600 text-white px-4 py-2 rounded flex items-center gap-2"
          >
            <Save size={16} /> Save
          </button>
          <button
            onClick={onCancel}
            className="bg-gray-400 text-white px-4 py-2 rounded flex items-center gap-2"
          >
            <X size={16} /> Cancel
          </button>
        </div>
      </div>
    );
  };

  // Handle Save
  const handleSave = async (type, data, isEdit) => {
    try {
      if (isEdit) {
        await adminAPI.update(type, data._id || data.id, data);
        showMessage('success', `${type} updated successfully!`);
      } else {
        await adminAPI.create(type, data);
        showMessage('success', `${type} created successfully!`);
      }
      setEditingItem(null);
      setShowAddForm(false);
      fetchData();
    } catch (error) {
      showMessage('error', `Failed to save: ${error.message}`);
    }
  };

  // Handle Delete
  const handleDelete = async (type, id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    try {
      await adminAPI.delete(type, id);
      showMessage('success', 'Deleted successfully!');
      fetchData();
    } catch (error) {
      showMessage('error', `Failed to delete: ${error.message}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <RefreshCw className="animate-spin" size={32} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-8">
      <div className="max-w-6xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-3xl font-bold text-gray-800">
              🛠️ ARCHIFAB Admin Panel
            </h1>
            <button
              onClick={handleLogout}
              className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded flex items-center gap-2 transition"
            >
              <Lock size={16} /> Logout
            </button>
          </div>

          {/* Message */}
          {message.text && (
            <div className={`p-4 rounded mb-4 ${
              message.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
            }`}>
              {message.text}
            </div>
          )}

          {/* Tabs */}
          <div className="flex gap-4 mb-6 border-b">
            <button
              onClick={() => { setActiveTab('team'); setShowAddForm(false); setEditingItem(null); }}
              className={`pb-2 px-4 flex items-center gap-2 ${
                activeTab === 'team' ? 'border-b-2 border-yellow-600 text-yellow-600' : 'text-gray-500'
              }`}
            >
              <Users size={18} /> Team ({team.length})
            </button>
            <button
              onClick={() => { setActiveTab('projects'); setShowAddForm(false); setEditingItem(null); }}
              className={`pb-2 px-4 flex items-center gap-2 ${
                activeTab === 'projects' ? 'border-b-2 border-yellow-600 text-yellow-600' : 'text-gray-500'
              }`}
            >
              <FolderOpen size={18} /> Projects ({projects.length})
            </button>
          </div>

          {/* Add Button */}
          <button
            onClick={() => { setShowAddForm(true); setEditingItem(null); }}
            className="bg-yellow-600 text-white px-4 py-2 rounded mb-4 flex items-center gap-2"
          >
            <Plus size={18} /> Add {activeTab === 'team' ? 'Team Member' : 'Project'}
          </button>

          {/* Add Form */}
          {showAddForm && activeTab === 'team' && (
            <TeamForm
              onSave={(data) => handleSave('team', data, false)}
              onCancel={() => setShowAddForm(false)}
            />
          )}
          {showAddForm && activeTab === 'projects' && (
            <ProjectForm
              onSave={(data) => handleSave('projects', data, false)}
              onCancel={() => setShowAddForm(false)}
            />
          )}

          {/* Team List */}
          {activeTab === 'team' && (
            <div className="space-y-4">
              {team.map((member) => (
                <div key={member._id || member.id} className="border rounded-lg p-4">
                  {editingItem === (member._id || member.id) ? (
                    <TeamForm
                      item={member}
                      onSave={(data) => handleSave('team', data, true)}
                      onCancel={() => setEditingItem(null)}
                    />
                  ) : (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <img
                          src={member.image}
                          alt={member.name}
                          className="w-16 h-16 rounded-full object-cover"
                          onError={(e) => e.target.src = 'https://via.placeholder.com/64'}
                        />
                        <div>
                          <h3 className="font-semibold">{member.name}</h3>
                          <p className="text-gray-500 text-sm">{member.role}</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setEditingItem(member._id || member.id)}
                          className="text-blue-600 p-2 hover:bg-blue-50 rounded"
                        >
                          <Edit size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete('team', member._id || member.id)}
                          className="text-red-600 p-2 hover:bg-red-50 rounded"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Projects List */}
          {activeTab === 'projects' && (
            <div className="space-y-4">
              {projects.map((project) => (
                <div key={project._id || project.id} className="border rounded-lg p-4">
                  {editingItem === (project._id || project.id) ? (
                    <ProjectForm
                      item={project}
                      onSave={(data) => handleSave('projects', data, true)}
                      onCancel={() => setEditingItem(null)}
                    />
                  ) : (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <img
                          src={project.images?.[0] || ''}
                          alt={project.title}
                          className="w-20 h-16 rounded object-cover"
                          onError={(e) => e.target.src = 'https://via.placeholder.com/80x64'}
                        />
                        <div>
                          <h3 className="font-semibold">{project.title}</h3>
                          <p className="text-gray-500 text-sm">
                            {project.category} • {project.location} • {project.year}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setEditingItem(project._id || project.id)}
                          className="text-blue-600 p-2 hover:bg-blue-50 rounded"
                        >
                          <Edit size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete('projects', project._id || project.id)}
                          className="text-red-600 p-2 hover:bg-red-50 rounded"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <p className="text-center text-gray-400 mt-4 text-sm">
          ARCHIFAB Admin Panel • Keep this URL private
        </p>
      </div>
    </div>
  );
};

export default Admin;
